import { useRef, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

const categories = [
  {
    id: 'appetizers', name: 'Street Food', count: '11 items', icon: 'ri-restaurant-line',
    accent: '#E8473F', bg: '#FFF0EE', image: 'https://readdy.ai/api/search-image?query=Indian%20street%20food%20samosa%20chaat%20colorful%20vibrant%20crispy%20appetizers%20overhead%20warm%20tones&width=300&height=300&seq=1401&orientation=squarish',
  },
  {
    id: 'tandoor', name: 'Tandoor', count: '7 items', icon: 'ri-fire-fill',
    accent: '#FF6B35', bg: '#FFF4EE', image: 'https://readdy.ai/api/search-image?query=Indian%20tandoori%20chicken%20tikka%20kebabs%20smoky%20charred%20clay%20oven%20platter%20close%20up%20overhead&width=300&height=300&seq=1402&orientation=squarish',
  },
  {
    id: 'curries', name: 'Curries', count: '18 items', icon: 'ri-bowl-line',
    accent: '#0D7377', bg: '#EEF8F8', image: 'https://readdy.ai/api/search-image?query=Indian%20curry%20multiple%20bowls%20overhead%20butter%20chicken%20tikka%20masala%20dal%20makhni%20colorful&width=300&height=300&seq=1403&orientation=squarish',
  },
  {
    id: 'biryani', name: 'Biryani', count: '6 items', icon: 'ri-bowl-fill',
    accent: '#FFB627', bg: '#FFFBEE', image: 'https://readdy.ai/api/search-image?query=Indian%20biryani%20saffron%20basmati%20rice%20copper%20handi%20pot%20overhead%20shot%20fragrant%20colorful&width=300&height=300&seq=1404&orientation=squarish',
  },
  {
    id: 'tandoori-breads', name: 'Breads', count: '8 items', icon: 'ri-bread-line',
    accent: '#C0177A', bg: '#FFF0F8', image: 'https://readdy.ai/api/search-image?query=Indian%20naan%20bread%20garlic%20butter%20tandoor%20basket%20overhead%20golden%20fluffy%20flatbread%20fresh&width=300&height=300&seq=1405&orientation=squarish',
  },
  {
    id: 'desserts', name: 'Desserts', count: '2 items', icon: 'ri-cake-line',
    accent: '#2C3E8B', bg: '#EFF1FF', image: 'https://readdy.ai/api/search-image?query=Indian%20dessert%20gulab%20jamun%20rose%20syrup%20vanilla%20ice%20cream%20elegant%20plate%20close%20up%20warm&width=300&height=300&seq=1406&orientation=squarish',
  },
];

export default function MenuPreview() {
  const [visible, setVisible] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.1 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section ref={ref} className="py-20 lg:py-28 bg-white">
      <div className="max-w-7xl mx-auto px-6">
        {/* Header */}
        <div className="text-center mb-14">
          <p className="font-sans text-xs uppercase tracking-[0.3em] text-saffron mb-3">Explore</p>
          <h2 className="font-serif text-4xl sm:text-5xl text-charcoal mb-4">Our Menu</h2>
          <p className="font-sans text-base text-charcoal/60 max-w-md mx-auto">
            From sizzling street bites to regal biryanis — every category is a new adventure
          </p>
        </div>

        {/* Category Grid */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
          {categories.map((cat, i) => (
            <Link
              key={cat.id}
              to={`/menu#${cat.id}`}
              className={`group relative rounded-2xl overflow-hidden cursor-pointer transition-all duration-700 hover:-translate-y-2 ${
                visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
              }`}
              style={{
                transitionDelay: `${i * 0.08}s`,
                backgroundColor: cat.bg,
                aspectRatio: '1/1',
              }}
            >
              {/* Background image */}
              <img
                src={cat.image}
                alt={cat.name}
                className="absolute inset-0 w-full h-full object-cover opacity-20 group-hover:opacity-30 transition-opacity"
              />

              {/* Content */}
              <div className="relative z-10 p-4 flex flex-col justify-between h-full">
                <div
                  className="w-10 h-10 flex items-center justify-center rounded-xl"
                  style={{ backgroundColor: `${cat.accent}20` }}
                >
                  <i className={`${cat.icon} text-xl`} style={{ color: cat.accent }}></i>
                </div>
                <div>
                  <h3 className="font-sans font-bold text-sm text-charcoal leading-tight">{cat.name}</h3>
                  <p className="font-sans text-xs mt-0.5" style={{ color: cat.accent }}>{cat.count}</p>
                </div>
                <div
                  className="w-6 h-6 flex items-center justify-center rounded-full ml-auto opacity-0 group-hover:opacity-100 transition-opacity"
                  style={{ backgroundColor: cat.accent }}
                >
                  <i className="ri-arrow-right-line text-white text-xs"></i>
                </div>
              </div>

              {/* Hover border */}
              <div
                className="absolute inset-0 rounded-2xl border-2 border-transparent group-hover:border-opacity-40 transition-all"
                style={{ borderColor: cat.accent }}
              ></div>
            </Link>
          ))}
        </div>

        <div className="text-center mt-12">
          <Link
            to="/menu"
            className="inline-flex items-center gap-2 bg-charcoal text-white font-sans font-semibold text-sm px-8 py-3.5 rounded-full hover:bg-saffron transition-colors duration-300 cursor-pointer whitespace-nowrap"
          >
            See Full Menu <i className="ri-menu-2-line"></i>
          </Link>
        </div>
      </div>
    </section>
  );
}
